// Email validation
// Requires a real TLD (letters only) so values like "user@gmail.com123" are rejected
export const validateEmail = (email) => {
  const value = typeof email === 'string' ? email.trim() : ''

  if (!value) {
    return { isValid: false, message: 'Email is required' }
  }

  if (/\s/.test(value)) {
    return { isValid: false, message: 'Email cannot contain spaces' }
  }

  if (value.length > 254) {
    return { isValid: false, message: 'Email address is too long' }
  }

  if (!value.includes('@')) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  const [localPart, ...domainParts] = value.split('@')
  const domain = domainParts.join('@')

  if (!localPart || !domain) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  if (localPart.length > 64) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..') || !domain.includes('.')) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/

  if (!emailRegex.test(value)) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  return { isValid: true, message: '' }
}

// Phone number validation (national number only — country code selected separately)
export const getNationalPhoneLimits = (countryCode = '') => {
  const code = String(countryCode || '').replace(/\D/g, '')
  const e164MaxNational = Math.max(4, 15 - (code.length || 0))

  const known = {
    1: { min: 10, max: 10 },
    92: { min: 10, max: 10 },
    44: { min: 10, max: 10 },
    91: { min: 10, max: 10 },
    61: { min: 9, max: 9 },
  }

  if (known[code]) return known[code]
  return { min: 7, max: e164MaxNational }
}

export const normalizeNationalPhoneDigits = (raw, countryCode = '') => {
  let digits = String(raw ?? '').replace(/\D/g, '')
  const codeDigits = String(countryCode || '').replace(/\D/g, '')

  if (codeDigits) {
    const with00 = `00${codeDigits}`
    if (digits.startsWith(with00)) {
      digits = digits.slice(with00.length)
    } else if (digits.startsWith(codeDigits)) {
      digits = digits.slice(codeDigits.length)
    }
  }

  if (digits.startsWith('0') && ['92', '44', '91', '61'].includes(codeDigits)) {
    digits = digits.replace(/^0+/, '')
  }

  const { max } = getNationalPhoneLimits(countryCode)
  return digits.slice(0, max)
}

export const validatePhone = (phone, countryCode = '') => {
  if (!phone) {
    return { isValid: false, message: 'Phone number is required' }
  }

  const digitsOnly = normalizeNationalPhoneDigits(phone, countryCode)

  if (!/^\d+$/.test(digitsOnly)) {
    return { isValid: false, message: 'Phone number must contain only digits' }
  }

  const { min, max } = getNationalPhoneLimits(countryCode)

  if (digitsOnly.length < min) {
    return {
      isValid: false,
      message:
        min === max
          ? `Phone number must be ${min} digits`
          : `Phone number must have at least ${min} digits`,
    }
  }

  if (digitsOnly.length > max) {
    return {
      isValid: false,
      message:
        min === max
          ? `Phone number must be ${max} digits`
          : `Phone number is too long (max ${max} digits)`,
    }
  }

  return { isValid: true, message: '' }
}

// Password validation
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: 'Password is required' }
  }
  if (password.length < 6) {
    return { isValid: false, message: 'Password must be at least 6 characters' }
  }
  return { isValid: true, message: '' }
}

export const validateConfirmPassword = (password, confirmPassword) => {
  if (!confirmPassword) {
    return { isValid: false, message: 'Please confirm your password' }
  }
  if (password !== confirmPassword) {
    return { isValid: false, message: 'Passwords do not match' }
  }
  return { isValid: true, message: '' }
}

export const validateGender = (gender) => {
  const allowed = ['Female', 'Male', 'Other', 'Prefer not to say']
  if (!gender || !String(gender).trim()) {
    return { isValid: false, message: 'Gender is required' }
  }
  if (!allowed.includes(gender)) {
    return { isValid: false, message: 'Please select a valid gender' }
  }
  return { isValid: true, message: '' }
}

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name) {
    return { isValid: false, message: `${fieldName} is required` }
  }
  if (name.trim().length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters` }
  }
  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return { isValid: false, message: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` }
  }
  return { isValid: true, message: '' }
}

// Date of Birth validation
// Rules:
// - Required (when enforced by caller)
// - Must be a valid date in YYYY-MM-DD format (from <input type="date">)
// - Must not be in the future
// - Must be at least minAge years old (default 13+)
export const parseISODateOnly = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null

  // Expect YYYY-MM-DD from HTML date inputs
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString.trim())
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null

  // Construct at local midnight to avoid timezone shifting issues
  const dt = new Date(year, month - 1, day)

  // Validate that JS didn't roll the date (e.g., 2026-02-31)
  if (
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null
  }

  return dt
}

export const formatDateInputValue = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return ''
  const yyyy = String(dateObj.getFullYear())
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0')
  const dd = String(dateObj.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const DOB_MIN_AGE_YEARS = 13
export const DOB_MAX_AGE_YEARS = 150

export const getLatestAllowedDob = (minAge = DOB_MIN_AGE_YEARS) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate())
}

export const getEarliestAllowedDob = (maxAge = DOB_MAX_AGE_YEARS) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return new Date(today.getFullYear() - maxAge, today.getMonth(), today.getDate())
}

export const validateDateOfBirth = (dateString, { minAge = DOB_MIN_AGE_YEARS, maxAge = DOB_MAX_AGE_YEARS, required = true } = {}) => {
  if (!dateString) {
    return required
      ? { isValid: false, message: 'Date of Birth is required' }
      : { isValid: true, message: '' }
  }

  const dob = parseISODateOnly(dateString)
  if (!dob) {
    return { isValid: false, message: 'Please enter a valid Date of Birth' }
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  if (dob.getTime() > today.getTime()) {
    return { isValid: false, message: 'Date of Birth cannot be in the future' }
  }

  // Must be born on or before (today - minAge years) when minAge > 0
  if (minAge > 0) {
    const latestAllowedDob = getLatestAllowedDob(minAge)
    if (dob.getTime() > latestAllowedDob.getTime()) {
      return { isValid: false, message: `You must be at least ${minAge} years old` }
    }
  }

  const earliestAllowedDob = getEarliestAllowedDob(maxAge)
  if (dob.getTime() < earliestAllowedDob.getTime()) {
    return { isValid: false, message: 'Please enter a valid date of birth' }
  }

  return { isValid: true, message: '' }
}












